import { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Input,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBack,
  Castle,
  People,
  AttachMoney,
  TrendingUp,
  Edit,
  Delete,
  CloudUpload,
  Link,
  Token,
} from '@mui/icons-material';
import NationManagement from './NationManagement';
import PlayerRelations from './PlayerRelations';
import EconomicManagement from './EconomicManagement';
import AdminPanel from './AdminPanel';
import CharacterAvatar from './CharacterAvatar';
import PlayerSkills from './PlayerSkills';
import TokenCreator from './TokenCreator';
import { getApiUrl } from '../config';
import {
  GlassBackground,
  GlassContainer,
  GlassCard,
  GlassButton,
  GlassIconButton,
  GlassChip,
  GlassInfoCard,
  useGlassShimmer,
  GlassShimmer,
} from './ui/GlassComponents';
import {
  GlassPageHeader,
} from './ui/GlassLayoutComponents';

const GameDashboard = ({ authData, sessionData, onBack, onLogout, onRefreshCharacterData }) => {
  const [currentView, setCurrentView] = useState('dashboard'); // 'dashboard', 'nations', 'players', 'economy', 'stats'
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const shimmerRef = useGlassShimmer('.glass-container, .management-card');
  const [profilePictureDialogOpen, setProfilePictureDialogOpen] = useState(false);
  const [profilePictureUrl, setProfilePictureUrl] = useState('');
  const [profilePictureError, setProfilePictureError] = useState('');
  const [profilePictureLoading, setProfilePictureLoading] = useState(false);
  const [profilePictureTab, setProfilePictureTab] = useState(0); // 0 = upload, 1 = URL
  const [selectedFile, setSelectedFile] = useState(null);
  const [biographyDialogOpen, setBiographyDialogOpen] = useState(false);
  const [biography, setBiography] = useState('');
  const [biographyError, setBiographyError] = useState('');
  const [biographyLoading, setBiographyLoading] = useState(false);
  const [tokenCreatorOpen, setTokenCreatorOpen] = useState(false);

  // Use computed values that update when sessionData changes
  const character = sessionData?.character;
  const selectedDisplayName = sessionData?.selectedDisplayName;
  const isAdminPanel = sessionData?.isAdminPanel;

  if (!character || !selectedDisplayName) {
    return (
      <GlassBackground>
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <GlassContainer>
            <GlassInfoCard severity="error">
              Character data not found. Please go back and select a character.
            </GlassInfoCard>
          </GlassContainer>
        </Container>
      </GlassBackground>
    );
  }

  // If this is admin panel mode, render the admin panel instead
  if (isAdminPanel) {
    return (
      <AdminPanel
        authData={authData}
        sessionData={sessionData}
        onBack={onBack}
      />
    );
  }

  const handleSetProfilePicture = async () => {
    if (!profilePictureUrl.trim()) {
      setProfilePictureError('Please enter a valid URL');
      return;
    }

    setProfilePictureLoading(true);
    setProfilePictureError('');

    try {
      const response = await fetch(getApiUrl(`/players/${selectedDisplayName}/profile-picture`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`
        },
        body: JSON.stringify({
          profilePictureUrl: profilePictureUrl.trim()
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setProfilePictureDialogOpen(false);
        setProfilePictureUrl('');
        
        // Update the session data immediately to reflect the change
        const updatedSessionData = {
          ...sessionData,
          character: {
            ...sessionData.character,
            profilePictureUrl: profilePictureUrl.trim()
          }
        };
        
        // Update the session data in the parent component
        if (onRefreshCharacterData) {
          await onRefreshCharacterData(updatedSessionData);
        }
      } else {
        setProfilePictureError(result.message || 'Failed to set profile picture');
      }
    } catch (err) {
      setProfilePictureError('Failed to connect to server');
    } finally {
      setProfilePictureLoading(false);
    }
  };

  const handleUploadProfilePicture = async () => {
    if (!selectedFile) {
      setProfilePictureError('Please select a file');
      return;
    }

    setProfilePictureLoading(true);
    setProfilePictureError('');

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const response = await fetch(getApiUrl(`/players/${selectedDisplayName}/profile-picture/upload`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionData.token}`
        },
        body: formData
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setProfilePictureDialogOpen(false);
        setSelectedFile(null);
        setProfilePictureUrl('');
        
        // Update the session data immediately to reflect the change
        const updatedSessionData = {
          ...sessionData,
          character: {
            ...sessionData.character,
            profilePictureUrl: result.imageUrl
          }
        };
        
        // Update the session data in the parent component
        if (onRefreshCharacterData) {
          await onRefreshCharacterData(updatedSessionData);
        }
      } else {
        setProfilePictureError(result.message || 'Failed to upload profile picture');
      }
    } catch (err) {
      setProfilePictureError('Failed to connect to server');
    } finally {
      setProfilePictureLoading(false);
    }
  };

  const handleRemoveProfilePicture = async () => {
    setProfilePictureLoading(true);
    setProfilePictureError('');

    try {
      const response = await fetch(getApiUrl(`/players/${selectedDisplayName}/profile-picture`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionData.token}`
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setProfilePictureDialogOpen(false);
        setProfilePictureUrl('');
        setSelectedFile(null);
        
        // Update the session data immediately to reflect the removal
        const updatedSessionData = {
          ...sessionData,
          character: {
            ...sessionData.character,
            profilePictureUrl: null
          }
        };
        
        // Update the session data in the parent component
        if (onRefreshCharacterData) {
          await onRefreshCharacterData(updatedSessionData);
        }
      } else {
        setProfilePictureError(result.message || 'Failed to remove profile picture');
      }
    } catch (err) {
      setProfilePictureError('Failed to connect to server');
    } finally {
      setProfilePictureLoading(false);
    }
  };

  const handleSetBiography = async () => {
    setBiographyLoading(true);
    setBiographyError('');

    try {
      const response = await fetch(getApiUrl(`/players/${selectedDisplayName}/biography`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.token}`
        },
        body: JSON.stringify({
          biography: biography.trim()
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setBiographyDialogOpen(false);
        setBiography('');
        
        // Update the session data immediately to reflect the change
        const updatedSessionData = {
          ...sessionData,
          character: {
            ...sessionData.character,
            biography: biography.trim()
          }
        };
        
        // Update the session data in the parent component
        if (onRefreshCharacterData) {
          // Call the refresh function with the updated session data
          await onRefreshCharacterData(updatedSessionData);
        }
      } else {
        setBiographyError(result.message || 'Failed to set biography');
      }
    } catch (err) {
      setBiographyError('Failed to connect to server');
    } finally {
      setBiographyLoading(false);
    }
  };

  const handleRemoveBiography = async () => {
    setBiographyLoading(true);
    setBiographyError('');

    try {
      const response = await fetch(getApiUrl(`/players/${selectedDisplayName}/biography`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionData.token}`
        }
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setBiographyDialogOpen(false);
        setBiography('');
        
        // Update the session data immediately to reflect the removal
        const updatedSessionData = {
          ...sessionData,
          character: {
            ...sessionData.character,
            biography: null
          }
        };
        
        // Update the session data in the parent component
        if (onRefreshCharacterData) {
          await onRefreshCharacterData(updatedSessionData);
        }
      } else {
        setBiographyError(result.message || 'Failed to remove biography');
      }
    } catch (err) {
      setBiographyError('Failed to connect to server');
    } finally {
      setBiographyLoading(false);
    }
  };

  const openBiographyDialog = () => {
    setBiography(character.biography || '');
    setBiographyError('');
    setBiographyDialogOpen(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setProfilePictureError('Please select a PNG, JPEG, or WebP image file.');
        return;
      }

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setProfilePictureError('File size must be less than 5MB.');
        return;
      }

      setSelectedFile(file);
      setProfilePictureError('');
    }
  };

  const resetProfilePictureDialog = () => {
    setProfilePictureUrl('');
    setSelectedFile(null);
    setProfilePictureError('');
    setProfilePictureTab(0);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleDateString();
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const then = new Date(timestamp);
    const diffDays = Math.floor((now - then) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  const renderContent = () => {
    switch (currentView) {
      case 'nations':
        return (
          <NationManagement
            authData={authData}
            sessionData={sessionData}
            onBack={() => setCurrentView('dashboard')}
          />
        );
      case 'players':
        return (
          <PlayerRelations
            authData={authData}
            sessionData={sessionData}
            onBack={() => setCurrentView('dashboard')}
          />
        );
      case 'economy':
        return (
          <EconomicManagement
            authData={authData}
            sessionData={sessionData}
            onBack={() => setCurrentView('dashboard')}
          />
        );
      case 'stats':
        return (
          <GlassBackground>
            <GlassShimmer ref={shimmerRef} />
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
              <GlassContainer className="glass-container">
                <GlassPageHeader
                  title="Character Skills"
                  subtitle={`Skills and progress for ${selectedDisplayName}`}
                  action={
                    <GlassIconButton onClick={() => setCurrentView('dashboard')}>
                      <ArrowBack />
                    </GlassIconButton>
                  }
                />
                <PlayerSkills
                  displayName={selectedDisplayName}
                  jwt={sessionData.token}
                />
              </GlassContainer>
            </Container>
          </GlassBackground>
        );
      default:
        return renderDashboard();
    }
  };

  const renderDashboard = () => (
    <GlassBackground>
      <GlassShimmer ref={shimmerRef} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
        <GlassContainer className="glass-container">
          {/* Header Section */}
          <GlassPageHeader
            title="Character Dashboard"
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <GlassIconButton
                  onClick={() => setTokenCreatorOpen(true)}
                  title="Create API Token"
                >
                  <Token />
                </GlassIconButton>
                <GlassIconButton onClick={onBack}>
                  <ArrowBack />
                </GlassIconButton>
              </Box>
            }
          />

          {/* Character Info & Biography Row */}
          <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 4 }}>
            {/* Character Info */}
            <Grid item xs={12} lg={8}>
              <GlassCard className="glass-card">
                <Box sx={{ p: isMobile ? 3 : 4 }}>
                  <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'center' : 'flex-start' }}>
                    <Box sx={{ position: 'relative', mb: isMobile ? 2 : 0 }}>
                      <CharacterAvatar
                        key={character.profilePictureUrl || 'no-picture'}
                        profilePictureUrl={character.profilePictureUrl}
                        characterName={selectedDisplayName}
                        size={isMobile ? 80 : 100}
                        isRecent={character.isRecent}
                        sx={{
                          mr: isMobile ? 0 : 3,
                          border: '2px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        }}
                      />
                      <GlassIconButton
                        size="small"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: isMobile ? 10 : 20,
                        }}
                        onClick={() => setProfilePictureDialogOpen(true)}
                      >
                        <Edit fontSize="small" />
                      </GlassIconButton>
                    </Box>
                    <Box sx={{ flexGrow: 1, textAlign: isMobile ? 'center' : 'left' }}>
                      <Typography
                        variant={isMobile ? "h5" : "h4"}
                        component="h1"
                        gutterBottom
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          mb: 2
                        }}
                      >
                        {selectedDisplayName}
                      </Typography>
                      {character.isRecent && (
                        <GlassChip
                          variant="success"
                          label="Most Recent Character"
                          sx={{ mb: 2 }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255, 255, 255, 0.7)',
                          mt: 1
                        }}
                      >
                        Last seen: {getTimeSince(character.lastSeen)}
                      </Typography>
                      {character.firstUsed && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.7)'
                          }}
                        >
                          First used: {formatDate(character.firstUsed)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </GlassCard>
            </Grid>

            {/* Biography */}
            <Grid item xs={12} lg={4}>
              <GlassCard className="glass-card">
                <Box sx={{ p: isMobile ? 3 : 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        fontWeight: 600
                      }}
                    >
                      Biography
                    </Typography>
                    <GlassIconButton size="small" onClick={openBiographyDialog}>
                      <Edit fontSize="small" />
                    </GlassIconButton>
                  </Box>
                  {character.biography ? (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.6,
                        maxHeight: '120px',
                        overflowY: 'auto'
                      }}
                    >
                      {character.biography}
                    </Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontStyle: 'italic'
                      }}
                    >
                      No biography set. Click the edit button to add one.
                    </Typography>
                  )}
                </Box>
              </GlassCard>
            </Grid>
          </Grid>

          {/* Game Management Section */}
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: 'white',
              fontWeight: 600,
              mb: 3,
              textAlign: isMobile ? 'center' : 'left'
            }}
          >
            Game Management
          </Typography>

          {/* Management Cards - Grid on mobile, flex on desktop */}
          <Box
            sx={{
              display: isMobile ? 'grid' : 'flex',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : undefined,
              gap: isMobile ? 1.5 : 3,
            }}
          >
            {/* Nation Management */}
            <GlassCard
              className="management-card"
              clickable
              onClick={() => setCurrentView('nations')}
              sx={{
                flex: isMobile ? undefined : '1',
              }}
            >
              <Box sx={{ textAlign: 'center', p: isMobile ? 2 : 3 }}>
                <Castle sx={{
                  fontSize: isMobile ? '2rem' : '3rem',
                  color: 'rgba(174, 1, 1, 0.9)',
                  mb: isMobile ? 0 : 2
                }} />
                {!isMobile && (
                  <>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.25rem'
                      }}
                    >
                      Nation Management
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        mb: 3,
                        lineHeight: 1.5
                      }}
                    >
                      Create, join, and manage nations. Build diplomatic relationships.
                    </Typography>
                  </>
                )}
              </Box>
            </GlassCard>

            {/* Player Relations */}
            <GlassCard
              className="management-card"
              clickable
              onClick={() => setCurrentView('players')}
              sx={{
                flex: isMobile ? undefined : '1',
              }}
            >
              <Box sx={{ textAlign: 'center', p: isMobile ? 2 : 3 }}>
                <People sx={{
                  fontSize: isMobile ? '2rem' : '3rem',
                  color: 'rgba(75, 0, 149, 0.9)',
                  mb: isMobile ? 0 : 2
                }} />
                {!isMobile && (
                  <>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.25rem'
                      }}
                    >
                      Player Relations
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        mb: 3,
                        lineHeight: 1.5
                      }}
                    >
                      Manage friendships, alliances, and diplomatic relationships.
                    </Typography>
                  </>
                )}
              </Box>
            </GlassCard>

            {/* Economic System */}
            <GlassCard
              className="management-card"
              clickable
              onClick={() => setCurrentView('economy')}
              sx={{
                flex: isMobile ? undefined : '1',
              }}
            >
              <Box sx={{ textAlign: 'center', p: isMobile ? 2 : 3 }}>
                <AttachMoney sx={{
                  fontSize: isMobile ? '2rem' : '3rem',
                  color: 'rgba(255, 193, 7, 0.9)',
                  mb: isMobile ? 0 : 2
                }} />
                {!isMobile && (
                  <>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.25rem'
                      }}
                    >
                      Banking & Finance
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        mb: 3,
                        lineHeight: 1.5
                      }}
                    >
                      Manage your banks, currencies, and economic activities.
                    </Typography>
                  </>
                )}
              </Box>
            </GlassCard>

            {/* Character Skills */}
            <GlassCard
              className="management-card"
              clickable
              onClick={() => setCurrentView('stats')}
              sx={{
                flex: isMobile ? undefined : '1',
              }}
            >
              <Box sx={{ textAlign: 'center', p: isMobile ? 2 : 3 }}>
                <TrendingUp sx={{
                  fontSize: isMobile ? '2rem' : '3rem',
                  color: 'rgba(76, 175, 80, 0.9)',
                  mb: isMobile ? 0 : 2
                }} />
                {!isMobile && (
                  <>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '1.25rem'
                      }}
                    >
                      Character Skills
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        mb: 3,
                        lineHeight: 1.5
                      }}
                    >
                      View your skills, progress, and character statistics.
                    </Typography>
                  </>
                )}
              </Box>
            </GlassCard>
          </Box>
        </GlassContainer>
      </Container>
    </GlassBackground>
  );

  return (
    <>
      {renderContent()}

      {/* Profile Picture Dialog */}
      <Dialog 
        open={profilePictureDialogOpen} 
        onClose={() => {
          setProfilePictureDialogOpen(false);
          resetProfilePictureDialog();
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Manage Profile Picture</DialogTitle>
        <DialogContent>
          {character.profilePictureUrl && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Current Profile Picture:</Typography>
              <CharacterAvatar
                key={character.profilePictureUrl || 'no-picture-dialog'}
                profilePictureUrl={character.profilePictureUrl}
                characterName={selectedDisplayName}
                size={100}
                sx={{ mx: 'auto' }}
              />
            </Box>
          )}

          <Tabs
            value={profilePictureTab}
            onChange={(_e, newValue) => setProfilePictureTab(newValue)}
            sx={{ mb: 2 }}
          >
            <Tab 
              icon={<CloudUpload />} 
              label="Upload Image" 
              iconPosition="start"
            />
            <Tab 
              icon={<Link />} 
              label="Use URL" 
              iconPosition="start"
            />
          </Tabs>

          {profilePictureTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload an image file for {selectedDisplayName}. Supported formats: PNG, JPEG, WebP. Maximum size: 5MB.
              </Typography>
              
              <Input
                type="file"
                inputProps={{ 
                  accept: "image/png,image/jpeg,image/jpg,image/webp",
                  onChange: handleFileSelect
                }}
                sx={{ mb: 2 }}
                fullWidth
                disabled={profilePictureLoading}
              />

              {selectedFile && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="body2">
                    Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {profilePictureTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Set a profile picture URL for {selectedDisplayName}. Must be a valid HTTP/HTTPS URL ending in .png, .jpg, .jpeg, .gif, or .webp
              </Typography>
          
          <TextField
            fullWidth
            label="Profile Picture URL"
            value={profilePictureUrl}
            onChange={(e) => setProfilePictureUrl(e.target.value)}
            placeholder="https://example.com/image.png"
            sx={{ mb: 2 }}
            disabled={profilePictureLoading}
          />
            </Box>
          )}
          
          {profilePictureError && (
            <GlassInfoCard severity="error" sx={{ mb: 2 }}>
              {profilePictureError}
            </GlassInfoCard>
          )}
        </DialogContent>
        <DialogActions>
          {character.profilePictureUrl && (
            <Button
              onClick={handleRemoveProfilePicture}
              color="error"
              disabled={profilePictureLoading}
              startIcon={<Delete />}
            >
              Remove
            </Button>
          )}
          <Button 
            onClick={() => {
              setProfilePictureDialogOpen(false);
              resetProfilePictureDialog();
            }} 
            disabled={profilePictureLoading}
          >
            Cancel
          </Button>
          {profilePictureTab === 0 ? (
            <Button
              onClick={handleUploadProfilePicture}
              variant="contained"
              disabled={profilePictureLoading || !selectedFile}
              startIcon={<CloudUpload />}
            >
              {profilePictureLoading ? 'Uploading...' : 'Upload'}
            </Button>
          ) : (
          <Button
            onClick={handleSetProfilePicture}
            variant="contained"
            disabled={profilePictureLoading || !profilePictureUrl.trim()}
              startIcon={<Link />}
          >
              {profilePictureLoading ? 'Setting...' : 'Set URL'}
          </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Biography Dialog */}
      <Dialog open={biographyDialogOpen} onClose={() => setBiographyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Biography</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Biography"
            fullWidth
            multiline
            rows={6}
            variant="outlined"
            value={biography}
            onChange={(e) => setBiography(e.target.value)}
            error={!!biographyError}
            helperText={biographyError || `${biography.length}/1000 characters`}
            inputProps={{ maxLength: 1000 }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBiographyDialogOpen(false)} disabled={biographyLoading}>
            Cancel
          </Button>
          {character.biography && (
            <Button onClick={handleRemoveBiography} disabled={biographyLoading} color="error">
              {biographyLoading ? 'Removing...' : 'Remove'}
            </Button>
          )}
          <Button onClick={handleSetBiography} disabled={biographyLoading || biography.trim() === ''}>
            {biographyLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Token Creator Dialog */}
      <TokenCreator
        sessionData={sessionData}
        authData={authData}
        open={tokenCreatorOpen}
        onClose={() => setTokenCreatorOpen(false)}
      />
    </>
  );
};

export default GameDashboard; 